import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { api } from '../../services/api';
import { trackPasswordResetCompleted } from '../../utils/analytics';
import { log } from '../../utils/logger';
import './AuthModal.css';

interface ResetPasswordPageProps {
  token: string;
  onComplete: () => void;
}

function ResetPasswordPage({ token, onComplete }: ResetPasswordPageProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<'form' | 'loading' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    log.info('ResetPasswordPage', 'Mounted');
  }, []);

  const markTouched = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const pwReqs = password.length > 0 ? {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  } : null;

  const isFormValid = useMemo(() => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      password === confirmPassword
    );
  }, [password, confirmPassword]);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (touched.password && password.length > 0) {
      const missing: string[] = [];
      if (password.length < 8) missing.push(t('auth.validation.min8'));
      if (!/[A-Z]/.test(password)) missing.push(t('auth.validation.uppercase'));
      if (!/[a-z]/.test(password)) missing.push(t('auth.validation.lowercase'));
      if (!/[0-9]/.test(password)) missing.push(t('auth.validation.number'));
      if (missing.length > 0) {
        errors.password = missing.join(' \u00b7 ');
      }
    }
    if (touched.confirmPassword && confirmPassword.length > 0 && password !== confirmPassword) {
      errors.confirmPassword = t('auth.errors.passwordsNoMatch');
    }
    return errors;
  }, [password, confirmPassword, touched, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    log.info('ResetPasswordPage', 'Form submitted');
    setStatus('loading');
    setErrorMessage('');

    try {
      await api.resetPassword(token, password);
      setStatus('success');
      log.info('ResetPasswordPage', 'Password reset success');
      trackPasswordResetCompleted();
    } catch (error) {
      setStatus('error');
      log.error('ResetPasswordPage', 'Password reset error', error);
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('expired')) {
        setErrorMessage(t('auth.resetExpired'));
      } else {
        setErrorMessage(t('auth.resetInvalid'));
      }
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">
        <h2>{t('auth.resetPassword')}</h2>

        {status === 'success' && (
          <>
            <p className="verify-email-success">{t('auth.resetSuccess')}</p>
            <button
              className="auth-submit"
              onClick={onComplete}
              style={{ marginTop: '16px' }}
            >
              {t('auth.signIn')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <p className="verify-email-error">{errorMessage}</p>
            <button
              className="auth-submit"
              onClick={onComplete}
              style={{ marginTop: '16px' }}
            >
              {t('auth.backToLogin')}
            </button>
          </>
        )}

        {(status === 'form' || status === 'loading') && (
          <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: 'var(--spacing-md)', textAlign: 'center' }}>
              {t('auth.resetPasswordDesc')}
            </p>
            <div className="auth-field">
              <label htmlFor="newPassword">{t('auth.newPassword')}</label>
              <div className="auth-password-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => markTouched('password')}
                  placeholder={t('auth.placeholders.atLeast8Chars')}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  autoFocus
                  className={fieldErrors.password ? 'auth-input-error' : ''}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.password && pwReqs && (
                <div className="auth-pw-requirements">
                  <span className={pwReqs.length ? 'met' : ''}>8+</span>
                  <span className={pwReqs.upper ? 'met' : ''}>A-Z</span>
                  <span className={pwReqs.lower ? 'met' : ''}>a-z</span>
                  <span className={pwReqs.number ? 'met' : ''}>0-9</span>
                </div>
              )}
            </div>
            <div className="auth-field">
              <label htmlFor="confirmNewPassword">{t('auth.confirmPassword')}</label>
              <div className="auth-password-wrapper">
                <input
                  id="confirmNewPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => markTouched('confirmPassword')}
                  placeholder={t('auth.placeholders.confirmYourPassword')}
                  required
                  autoComplete="new-password"
                  className={fieldErrors.confirmPassword ? 'auth-input-error' : ''}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.confirmPassword && <span className="auth-field-error">{fieldErrors.confirmPassword}</span>}
            </div>
            <button
              type="submit"
              className="auth-submit"
              disabled={status === 'loading' || !isFormValid}
            >
              {status === 'loading' ? <span className="auth-spinner" /> : t('auth.resetPassword')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
